/* eslint-disable complexity */
import classNames from 'classnames'
import { useTracking } from 'lib/analytics'
import useCurrentUser from 'shared/hooks/useCurrentUser/useCurrentUser'
import {
  CardDesktopVariant,
  CardImageShape,
  Card as CardType,
  Link,
} from '../../../../LandingPage.types'
import css from './Card.styles.scss'
import { LinkArrowIcon } from '../../../../LandingPage.icons'

interface CardProps {
  card: CardType
  desktopVariant: CardDesktopVariant
  imageShape: CardImageShape
  listView: boolean
}

const Card = ({ card, desktopVariant, imageShape, listView }: CardProps) => {
  const [currentUser] = useCurrentUser()
  const tracker = useTracking()

  const renderLink = ({ url, text, onClick }: Link) => {
    if (url || onClick) {
      return (
        <a
          className={css.Card__link}
          href={url}
          onClick={() => {
            if (typeof onClick === 'function') {
              onClick(currentUser, tracker)
            }
          }}
        >
          <span>{text}</span>
          <LinkArrowIcon className={css.Card__linkArrow} />
        </a>
      )
    }

    return (
      <i className={classNames(css.Card__link, css.Card__italicize)}>
        <span>{text}</span>
      </i>
    )
  }

  // There are very specific styles dependent on the interaction of these two options
  const isTwoColumns = desktopVariant === 'twoColumns'
  const isLargeRectangle = imageShape === 'largeRectangle'
  const containerConditionalClassNames = {
    [css.Card__listView]: listView,
  }
  if (isLargeRectangle) {
    containerConditionalClassNames[css['Card__container--largeRectangle']] =
      true
    if (isTwoColumns) {
      containerConditionalClassNames[css['Card__container--desktopGap48']] =
        true
      containerConditionalClassNames[
        css['Card__container--desktopMaxWidth440']
      ] = true
    } else {
      containerConditionalClassNames[css['Card__container--desktopGap24']] =
        true
      containerConditionalClassNames[
        css['Card__container--desktopMaxWidth300']
      ] = true
    }
  } else {
    containerConditionalClassNames[css['Card__container--desktopGap24']] = true
    if (isTwoColumns) {
      containerConditionalClassNames[
        css['Card__container--desktopMaxWidth460']
      ] = true
    } else {
      containerConditionalClassNames[
        css['Card__container--desktopMaxWidth260']
      ] = true
    }
  }

  return (
    <div
      className={classNames(
        css.Card__container,
        containerConditionalClassNames
      )}
    >
      {card.imageUrl && (
        <div
          className={classNames(css.Card__imageContainer, {
            [css.Card__imageContainerListView]: listView,
          })}
        >
          <img
            alt={card.altText || 'card'}
            className={css[`Card__${imageShape}`]}
            src={card.imageUrl}
          />
        </div>
      )}
      <div className={css.Card__contentContainer}>
        <div className={css.Card__textContainer}>
          <div
            className={classNames(css.Card__headerText, {
              [css.Card__colorPrimary]: imageShape === 'largeRectangle',
            })}
            dangerouslySetInnerHTML={{ __html: card.headerText }}
          />
          {card.subHeaderContent && (
            <div
              className={css.Card__subHeaderText}
              dangerouslySetInnerHTML={{ __html: card.subHeaderContent }}
            />
          )}
          {card.body && (
            <div
              className={css.Card__body}
              dangerouslySetInnerHTML={{ __html: card.body }}
            />
          )}
        </div>
        {card.link && renderLink(card.link)}
        {card.socialLinks && (
          <div className={css.Card__socialLinks}>
            {card.socialLinks.map(({ url, imageComponent, id }) => (
              <a
                className={css.Card__socialLink}
                href={url}
                key={id}
                rel="noreferrer"
                target="_blank"
              >
                {imageComponent}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default Card
